import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, PenLine, BadgeCheck, Download } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export function ContractDialog({ campaignId, creatorId, triggerClassName, triggerTestId }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const q = creatorId ? `?creator_id=${creatorId}` : "";
  const load = useCallback(() => {
    api.get(`/campaigns/${campaignId}/contract${q}`).then(({ data }) => setData(data)).catch(() => {});
  }, [campaignId, q]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const sign = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${campaignId}/contract/sign`);
      toast.success(t("contractSigned"));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("lc_token") || "";
    const cq = creatorId ? `&creator_id=${creatorId}` : "";
    window.open(`${api.defaults.baseURL}/campaigns/${campaignId}/contract/pdf?auth=${token}${cq}`, "_blank");
  };

  const isHtmlContract = /<\/?[a-z][\s\S]*>/i.test(data?.contract || "");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button onClick={() => setOpen(true)} data-testid={triggerTestId} className={triggerClassName}>
        <FileText size={13} /> {t("contract")}
      </button>
      <DialogContent className="border-cardline bg-card2 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("contract")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("signContract")}</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto rounded-xl border border-cardline bg-ink2/50 p-3 font-sans text-xs leading-relaxed text-slate-200" data-testid="contract-text">
          {isHtmlContract
            ? <div className="contract-prose" dangerouslySetInnerHTML={{ __html: data.contract }} />
            : <pre className="whitespace-pre-wrap font-sans">{data?.contract || t("loading")}</pre>}
        </div>
        {data?.accepted ? (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500/15 py-2.5 text-sm font-semibold text-emerald-400" data-testid="contract-signed-badge">
              <BadgeCheck size={16} /> {t("contractSigned")} — {t("signedOn")} {(data.accepted_at || "").slice(0, 10)}
            </div>
            <button onClick={downloadPdf} data-testid="contract-pdf-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-cardline bg-ink2/60 py-2.5 text-sm font-semibold text-slate-200 hover:border-gold hover:text-gold">
              <Download size={15} /> {t("downloadPdf")}
            </button>
          </div>
        ) : (
          <button onClick={sign} disabled={busy} data-testid="contract-sign-btn"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gold py-3 font-bold text-ink disabled:opacity-60">
            <PenLine size={16} /> {t("signAccept")}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
